import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { waLink } from "../lib/constants";

const NAV = [
  { id: "inicio", label: "Inicio" },
  { id: "nosotros", label: "Nosotros" },
  { id: "servicios", label: "Servicios" },
  { id: "trabajos", label: "Trabajos" },
  { id: "contacto", label: "Contacto" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("inicio");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      // Active section detection
      const offsets = NAV.map((n) => {
        const el = document.getElementById(n.id);
        if (!el) return { id: n.id, top: Infinity };
        const r = el.getBoundingClientRect();
        return { id: n.id, top: r.top };
      });
      const current =
        offsets
          .filter((o) => o.top <= 120)
          .sort((a, b) => b.top - a.top)[0]?.id || "inicio";
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[var(--fb-bg)]/85 backdrop-blur-md border-b border-[var(--fb-line)]"
            : "bg-transparent"
        }`}
      >
        <div className="fb-container flex items-center justify-between h-16 md:h-20">
          <button
            data-testid="logo-home-btn"
            onClick={() => go("inicio")}
            className="flex items-center gap-3 group"
            aria-label="Ir al inicio"
          >
            <span className="h-11 w-11 md:h-12 md:w-12 rounded-xl flex items-center justify-center bg-[var(--fb-ink)] shrink-0 shadow-sm">
              <img
                src="/brand/icon.png"
                alt="Florentin Contractors"
                className="h-8 w-8 md:h-9 md:w-9 object-contain"
                width="36"
                height="36"
              />
            </span>
            <span
              className={`font-display text-[15px] md:text-lg font-semibold tracking-tight transition-colors leading-none ${
                scrolled ? "text-[var(--fb-ink)]" : "text-white"
              }`}
            >
              Florentin Contractors
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}-btn`}
                onClick={() => go(n.id)}
                className={`relative px-3.5 py-2 text-sm font-medium transition-colors ${
                  scrolled
                    ? active === n.id
                      ? "text-[var(--fb-accent)]"
                      : "text-[var(--fb-ink)] hover:text-[var(--fb-accent)]"
                    : active === n.id
                      ? "text-white"
                      : "text-white/85 hover:text-white"
                }`}
              >
                {n.label}
                {active === n.id && (
                  <span
                    className={`absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] rounded-full ${
                      scrolled ? "bg-[var(--fb-accent)]" : "bg-white"
                    }`}
                  />
                )}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              data-testid="nav-whatsapp-btn"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp !py-2.5 !px-4 !text-sm"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
              Hablar por WhatsApp
            </a>
          </div>

          <button
            data-testid="menu-toggle-btn"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border transition-colors ${
              scrolled
                ? "border-[var(--fb-line)] text-[var(--fb-ink)] bg-white"
                : "border-white/50 text-white bg-black/15 backdrop-blur"
            }`}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <div
        data-testid="mobile-menu"
        className={`mobile-menu fixed inset-0 z-40 md:hidden ${
          open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-[var(--fb-ink)]" />
        <div className="relative h-full flex flex-col pt-24 pb-10 px-6">
          <nav className="flex flex-col gap-1">
            {NAV.map((n, i) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}-btn`}
                onClick={() => go(n.id)}
                className="text-left py-4 border-b border-white/10 text-white font-display text-3xl font-medium tracking-tight"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {n.label}
              </button>
            ))}
          </nav>
          <div className="mt-auto pt-8">
            <a
              data-testid="mobile-whatsapp-btn"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp w-full"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.4} />
              Solicitar Presupuesto
            </a>
            <p className="text-white/60 text-xs mt-4 text-center">
              Respondemos rápido por WhatsApp
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
